<template>
  <div
    class="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50 flex flex-col"
  >
    <!-- Background glow -->
    <div class="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div class="absolute -top-32 -left-24 h-72 w-72 rounded-full bg-sky-400/15 blur-3xl" />
      <div class="absolute -bottom-40 -right-24 h-80 w-80 rounded-full bg-cyan-500/15 blur-3xl" />
    </div>

    <!-- Top bar -->
    <header
      class="w-full border-b border-zinc-200/70 dark:border-zinc-800/70 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-sm"
    >
      <div class="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <div
            class="h-8 w-8 rounded-2xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center"
          >
            <span class="text-[18px] font-semibold text-sky-500">NG</span>
          </div>
          <div class="leading-tight">
            <p class="text-xs uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
              NexGridCo
            </p>
            <p class="text-sm font-medium text-zinc-900 dark:text-zinc-50">
              Legal & Privacy
            </p>
          </div>
        </div>

        <p
          class="hidden sm:inline-flex text-[11px] font-medium tracking-[0.18em] uppercase text-zinc-500 dark:text-zinc-400"
        >
          Privacy Policy
        </p>
      </div>
    </header>

    <!-- Content -->
    <main class="flex-1 flex items-stretch justify-center">
      <div class="w-full max-w-4xl mx-auto px-4 py-8 lg:py-10">
        <!-- Header + tabs -->
        <div class="mb-5 lg:mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p class="text-[11px] uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
              Legal documents
            </p>
            <h1 class="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              Privacy Policy
            </h1>
            <p class="text-sm text-zinc-600 dark:text-zinc-400 max-w-xl">
              Learn how NexGridCo collects, uses, stores, and protects your personal information
              and project data.
            </p>
          </div>

          <!-- Tabs -->
          <nav
            class="inline-flex items-center gap-1 rounded-full border border-zinc-200/80 dark:border-zinc-800/80 bg-white/80 dark:bg-zinc-900/80 px-1 py-1 text-xs"
          >
            <button
              type="button"
              class="rounded-full px-3 py-1 font-medium transition-colors"
              :class="isActive('terms')
                ? 'bg-sky-500 text-white shadow-sm'
                : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100/70 dark:hover:bg-zinc-800/70'"
              @click="go('terms')"
            >
              Terms
            </button>
            <button
              type="button"
              class="rounded-full px-3 py-1 font-medium transition-colors"
              :class="isActive('privacy')
                ? 'bg-sky-500 text-white shadow-sm'
                : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100/70 dark:hover:bg-zinc-800/70'"
              @click="go('privacy')"
            >
              Privacy
            </button>
            <button
              type="button"
              class="rounded-full px-3 py-1 font-medium transition-colors"
              :class="isActive('dpa')
                ? 'bg-sky-500 text-white shadow-sm'
                : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100/70 dark:hover:bg-zinc-800/70'"
              @click="go('dpa')"
            >
              DPA
            </button>
          </nav>
        </div>

        <!-- Card -->
        <section
          class="rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 bg-white/95 dark:bg-[#05060a]/95 shadow-2xl shadow-zinc-900/10 p-5 sm:p-6"
        >
          <div class="space-y-4 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300 max-h-[70vh] overflow-y-auto pr-1">
            <p>
              This Privacy Policy explains how NexGridCo collects, uses, stores, and protects your
              personal information. We follow globally recognized privacy and data governance principles.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">1. Information We Collect</h2>
            <ul class="list-disc list-inside space-y-1">
              <li>Account information (name, email, authentication data).</li>
              <li>Usage data, analytics, device information.</li>
              <li>Saved palettes, projects, design files, and workspace content.</li>
            </ul>

            <h2 class="text-base sm:text-lg font-semibold mt-4">2. How We Use Your Data</h2>
            <p>We process your data to:</p>
            <ul class="list-disc list-inside space-y-1">
              <li>Provide and improve the platform.</li>
              <li>Maintain security, authentication, and fraud prevention.</li>
              <li>Store your palettes, collections, and design projects.</li>
              <li>Send essential communications about your account or subscription.</li>
            </ul>

            <h2 class="text-base sm:text-lg font-semibold mt-4">3. Data Sharing</h2>
            <p>
              We do not sell your personal data. We may share it with trusted service providers
              (hosting, analytics, billing) strictly to operate the platform.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">4. Global Data Practices</h2>
            <p>
              We apply privacy practices aligned with global standards, including transparency,
              purpose limitation, data minimization, and user rights.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">5. Your Rights</h2>
            <p>You may request, at any time:</p>
            <ul class="list-disc list-inside space-y-1">
              <li>Access to your data.</li>
              <li>Correction or deletion of personal information.</li>
              <li>Portability of your projects or account data.</li>
              <li>Account closure.</li>
            </ul>

            <h2 class="text-base sm:text-lg font-semibold mt-4">6. Data Security</h2>
            <p>
              We use strong organizational and technical measures to protect your information against
              unauthorized access, alteration, or loss.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">7. Data Retention</h2>
            <p>
              We retain your data only for as long as necessary to provide the Services or comply with
              operational, legal, or security requirements.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">8. Changes to This Policy</h2>
            <p>
              We may update this Policy when necessary. Your continued use of NexGridCo constitutes
              acceptance of the updated terms.
            </p>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const isActive = (name) => route.name === name
const go = (name) => {
  if (!isActive(name)) router.push({ name })
}
</script>
